import { motion } from "framer-motion";
import { useEffect, useState } from "react";

type Props = {
  size?: number;
  onSecretClicks?: () => void;
};

/**
 * Animated cauldron centerpiece — uses the brand PNG with bubbling overlay
 * and a pulsing glow. 5 clicks fires the easter egg callback.
 */
export function Cauldron({ size = 320, onSecretClicks }: Props) {
  const [clicks, setClicks] = useState(0);

  useEffect(() => {
    if (clicks >= 5 && onSecretClicks) {
      onSecretClicks();
      setClicks(0);
    }
  }, [clicks, onSecretClicks]);

  return (
    <motion.div
      className="relative pointer"
      style={{ width: size, height: size }}
      onClick={() => setClicks((c) => c + 1)}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.2 }}
    >
      {/* Underglow */}
      <div
        className="absolute -inset-12 -z-10 rounded-full blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(circle, rgba(155,61,245,0.7) 0%, rgba(155,61,245,0.2) 40%, transparent 70%)",
        }}
      />
      {/* Pulsing inner glow */}
      <motion.div
        className="absolute inset-0 -z-10"
        animate={{ opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(circle at center 65%, rgba(192,132,252,0.55) 0%, transparent 50%)",
        }}
      />

      {/* The cauldron itself, drifting (glow comes from the wrapper divs) */}
      <motion.img
        src="/cauld.png"
        alt="Cauldron Dev — pixel cauldron logo"
        className="pixelated w-full h-full animate-drift select-none drop-shadow-[0_0_28px_rgba(155,61,245,0.6)]"
        draggable={false}
        whileHover={{ rotate: -2, scale: 1.04 }}
        transition={{ type: "spring", stiffness: 200, damping: 12 }}
      />

      {/* Bubbles rising out of the spill */}
      {[...Array(8)].map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full pixelated"
          style={{
            left: `${30 + Math.random() * 40}%`,
            bottom: `${20 + Math.random() * 30}%`,
            width: `${6 + Math.random() * 10}px`,
            height: `${6 + Math.random() * 10}px`,
            background: i % 2 ? "#c084fc" : "#9b3df5",
            boxShadow: "0 0 8px rgba(192,132,252,0.8)",
            animation: `bubble-up ${3 + Math.random() * 3}s ${Math.random() * 4}s infinite ease-in`,
          }}
        />
      ))}
    </motion.div>
  );
}
